import React, { useState } from 'react'
import getConfig from 'next/config'
import {
  useRouter,
  ContactSellerReasonType,
  sellerRoutes,
  support,
} from '@src/routes'
import { ApolloError } from '@apollo/client'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MUILink from '@mui/material/Link'
import { Error } from '@mui/icons-material'
import { Link } from '@src/components/Link'
import SelectDtaqDealerModal from '@src/components/SelectDtaqDealerModal'
import { extractData } from '@utils/errorHandler'
import messages from '@utils/messages'
import { useCurrentUser } from '@utils/useCurrentUser'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import { AddToCartFormValues } from './ProductPriceAvailabilityFormBody'
import { TrackedAvailabilityMessage } from '../TrackedAvailabilityMessage/TrackedAvailabilityMessage'
import { useUserSession } from '@src/utils/useUserSession'

const {
  publicRuntimeConfig: { featureFlags },
} = getConfig()

export const useStyles = makeStyles()((theme) => {
  return {
    row: {
      display: 'flex',
    },
    rowFormatting: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(3, 0),
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      borderTop: `1px solid ${theme.palette.grey[300]}`,
    },
    errorText: {
      lineHeight: 1.75,
    },
    errorContact: {
      display: 'flex',
      alignItems: 'center',
    },
    seeDealerOptionsLink: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      padding: 0,
      marginLeft: theme.spacing(1),
      textTransform: 'capitalize',
    },
    centerJustified: {
      display: 'flex',
      justifyContent: 'center',
      fontWeight: 500,
    },
  }
})

const ProductPriceAvailabilityError: React.FC<{
  error?: ApolloError
  productNumber?: string
  isMarketplace?: boolean
  sellerName?: string
  marketplaceSellerId?: string
  marketplaceOfferId?: string
  materialPricing?: Record<string, unknown>[]
  isCenterJustified?: boolean
  handlePriceUpdateForDealer?: (
    dealerId: string | null,
    values: AddToCartFormValues,
    materialPricing: ValidMaterialPricingDetailFragment[],
    setFieldValue: (field: string, value: any) => void,
    setIsModalOpen: (value: React.SetStateAction<boolean>) => void,
    dealerName: Maybe<string> | undefined
  ) => Promise<void>
}> = ({
  error,
  productNumber,
  isMarketplace = false,
  sellerName = '',
  marketplaceSellerId = '',
  marketplaceOfferId = '',
  materialPricing,
  isCenterJustified = true,
  handlePriceUpdateForDealer,
}) => {
  const { classes, cx } = useStyles()
  const router = useRouter()
  const { isDTAQZuCustomer } = useCurrentUser()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const errorObject = error ? extractData(error).errors[0] : undefined
  const hideTechnicalServiceLink =
    errorObject?.misc?.hideTechnicalServiceLink !== undefined
      ? errorObject.misc.hideTechnicalServiceLink
      : true
  const mPartner =
    isMarketplace &&
    !!sellerName &&
    !!marketplaceSellerId &&
    !!marketplaceOfferId

  const showPreferredDealerSection =
    featureFlags.dealerCartEnabled && isDTAQZuCustomer

  const errorMessage =
    (errorObject?.code && messages[errorObject.code]) ||
    messages.PRICING_AND_AVAILABILITY_UNAVAILABLE

  const {
    userSession: { country },
  } = useUserSession()

  return (
    <div
      className={cx(classes.row, {
        [classes.rowFormatting]: isCenterJustified,
      })}
    >
      <Box mr={2}>
        <Error color="error" />
      </Box>
      <Box className={cx({ [classes.centerJustified]: isCenterJustified })}>
        <span className={cx({ [classes.errorText]: isCenterJustified })}>
          {errorObject?.code ===
          'EMD_MILLIPORE_PRICING_AND_INVENTORY_UNAVAILABLE' ? (
            country === 'US' ? (
              <TrackedAvailabilityMessage
                id="PRODUCT_SOLD_EXCLUSIVELY_BY_MILLIPORE_VIA_OFFLINE_CHANNELS"
                defaultMessage="This product is sold exclusively by Millipore via offline channels. {link}"
                values={{
                  link: (
                    <Link href={support.customerSupport()}>
                      <a>
                        <FormattedMessage
                          id="CONTACT_CUSTOMER_SERVICE_FIRST_UPPERCASE"
                          defaultMessage="Contact Customer Service"
                        />
                      </a>
                    </Link>
                  ),
                }}
              />
            ) : country === 'CA' ? (
              <TrackedAvailabilityMessage
                id="PRODUCT_ONLY_SOLD_VIA_OFFLINE_CHANNELS"
                defaultMessage="This product is only sold via offline channels. {link}"
                values={{
                  link: (
                    <Link href={support.customerSupport()}>
                      <a>
                        <FormattedMessage
                          id="PLEASE_CONTACT_CUSTOMER_SERVICE"
                          defaultMessage="Please Contact Customer Service"
                        />
                      </a>
                    </Link>
                  ),
                }}
              />
            ) : (
              <TrackedAvailabilityMessage
                {...errorMessage}
                values={{
                  link: <a href="http://www.emdmillipore.com/">Millipore</a>,
                }}
              />
            )
          ) : errorObject?.code ===
            'MERCK_MILLIPORE_PRICING_AND_INVENTORY_UNAVAILABLE' ? (
            <TrackedAvailabilityMessage
              {...errorMessage}
              values={{
                link: (
                  <a href="http://www.merckmillipore.com/">Merck Millipore</a>
                ),
              }}
            />
          ) : errorObject?.code ===
              'MM_PRICING_AND_INVENTORY_UNAVAILABLE_HAS_STOCK' ||
            errorObject?.code === 'MM_PRICING_AND_INVENTORY_UNAVAILABLE' ? (
            <TrackedAvailabilityMessage
              {...errorMessage}
              values={{
                productNumber: productNumber,
                link: (
                  <a
                    href="https://www.sigmaaldrich.com/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Sigma-Aldrich
                  </a>
                ),
              }}
            />
          ) : errorObject?.code === 'PRODUCT_ONLY_SOLD_VIA_OFFLINE_CHANNELS' ? (
            <TrackedAvailabilityMessage
              {...errorMessage}
              values={{
                link: (
                  <Link href={support.customerSupport()}>
                    <a>
                      <FormattedMessage
                        id="PLEASE_CONTACT_CUSTOMER_SERVICE"
                        defaultMessage="Please Contact Customer Service"
                      />
                    </a>
                  </Link>
                ),
              }}
            />
          ) : errorObject?.code === 'CUSTOMER_NUMBER_BLOCKED' ? (
            <TrackedAvailabilityMessage
              {...errorMessage}
              values={{
                customerNumber: errorObject?.paramList
                  ? errorObject?.paramList[0]
                  : '',
              }}
            />
          ) : productNumber ? (
            <TrackedAvailabilityMessage
              {...errorMessage}
              values={{ productNumber: <b>{productNumber}</b> }}
            />
          ) : (
            <TrackedAvailabilityMessage {...errorMessage} />
          )}
        </span>
        {!hideTechnicalServiceLink && !mPartner ? (
          <span>
            <span>&nbsp;</span>
            <Link href={support.customerSupport()} passHref>
              <MUILink color="primary" className={classes.errorText}>
                <FormattedMessage {...messages.CONTACT_TECHNICAL_SERVICE} />
              </MUILink>
            </Link>
          </span>
        ) : null}
        {mPartner ? (
          <span className={classes.errorContact}>
            <span>&nbsp;</span>
            <Link
              {...sellerRoutes.contact(
                sellerName,
                marketplaceSellerId,
                router.asPath,
                ContactSellerReasonType.OFFER_MESSAGING,
                marketplaceOfferId as string
              )}
            >
              <a>
                <FormattedMessage
                  {...messages.CONTACT_SELLER_WITH_NAME}
                  values={{
                    sellerName,
                  }}
                />
              </a>
            </Link>
          </span>
        ) : null}
        {showPreferredDealerSection && (
          <Button
            variant="text"
            className={classes.seeDealerOptionsLink}
            onClick={() => setIsModalOpen(true)}
          >
            <FormattedMessage {...messages.SEE_ALL_BUYING_OPTIONS} />
          </Button>
        )}
        {showPreferredDealerSection &&
          handlePriceUpdateForDealer &&
          isModalOpen && (
            <SelectDtaqDealerModal
              values={{}}
              materialPricing={
                materialPricing as ValidMaterialPricingDetailFragment[]
              }
              isSavingDealerPreference={false}
              setFieldValue={(value) => value}
              setIsModalOpen={setIsModalOpen}
              onDealerSelect={(selectedDealerId, selectedDealerName) =>
                handlePriceUpdateForDealer(
                  selectedDealerId,
                  {},
                  materialPricing as ValidMaterialPricingDetailFragment[],
                  (value) => value,
                  setIsModalOpen,
                  selectedDealerName
                )
              }
              open={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              disabled={false}
            />
          )}
      </Box>
    </div>
  )
}

export default ProductPriceAvailabilityError
